import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

/**
 * Sidebar nav glyphs (White signal v2, Paper → App → "App v3 · … · LP-aligned").
 *
 * Flat duotone marks on a 20px grid, drawn at 18px. Inactive rows use three
 * neutral tones; the active row inks every part and turns cut-outs white, so
 * the selected glyph reads as a solid mark on the white row. No orange, ever.
 */

type Tone = "faint" | "light" | "mid" | "soft" | "cut"

// Inactive tones (Paper): faint #E5E5E5 · light #D4D4D4 · mid #8A8A8A · soft #A3A3A3 · cut = canvas #F5F5F5.
// Hovering a nav row (group/nav) inks every tone one step, so the glyph answers the pointer with the label.
const INACTIVE: Record<Tone, string> = {
  faint: "fill-[#E5E5E5] stroke-[#E5E5E5] dark:fill-[#404040] dark:stroke-[#404040] group-hover/nav:fill-[#D4D4D4] group-hover/nav:stroke-[#D4D4D4] dark:group-hover/nav:fill-[#525252] dark:group-hover/nav:stroke-[#525252]",
  light: "fill-[#D4D4D4] stroke-[#D4D4D4] dark:fill-[#525252] dark:stroke-[#525252] group-hover/nav:fill-[#A3A3A3] group-hover/nav:stroke-[#A3A3A3] dark:group-hover/nav:fill-[#737373] dark:group-hover/nav:stroke-[#737373]",
  mid: "fill-[#8A8A8A] stroke-[#8A8A8A] dark:fill-[#A3A3A3] dark:stroke-[#A3A3A3] group-hover/nav:fill-[#404040] group-hover/nav:stroke-[#404040] dark:group-hover/nav:fill-[#E5E5E5] dark:group-hover/nav:stroke-[#E5E5E5]",
  soft: "fill-[#A3A3A3] stroke-[#A3A3A3] dark:fill-[#737373] dark:stroke-[#737373] group-hover/nav:fill-[#737373] group-hover/nav:stroke-[#737373] dark:group-hover/nav:fill-[#A3A3A3] dark:group-hover/nav:stroke-[#A3A3A3]",
  cut: "fill-sidebar stroke-sidebar group-hover/nav:fill-[#ECECEC] group-hover/nav:stroke-[#ECECEC] dark:group-hover/nav:fill-[#141414] dark:group-hover/nav:stroke-[#141414]",
}
// Active: every part ink, cut-outs take the white row colour.
const ACTIVE: Record<Tone, string> = {
  faint: "fill-foreground stroke-foreground",
  light: "fill-foreground stroke-foreground",
  mid: "fill-foreground stroke-foreground",
  soft: "fill-foreground stroke-foreground",
  cut: "fill-sidebar-accent stroke-sidebar-accent",
}

export type NavIconName =
  | "home"
  | "analytics"
  | "heatmaps"
  | "funnels"
  | "attribution"
  | "experiments"
  | "api"
  | "connectors"
  | "export"
  | "tools"
  | "resources"
  | "settings"
  // Goal marks (test planner), same grammar as the nav glyphs
  | "clicks"
  | "forms"
  | "revenue"

type Part = (tone: (t: Tone) => string) => ReactNode

const GLYPHS: Record<NavIconName, Part> = {
  home: (t) => (
    <>
      <path d="M3 9.2 10 3.5l7 5.7V16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" className={t("mid")} strokeWidth={0} />
      <rect x="8.5" y="12" width="3" height="5" rx="0.9" className={t("cut")} strokeWidth={0} />
    </>
  ),
  analytics: (t) => (
    <>
      <rect x="3" y="11" width="3.5" height="6" rx="1.2" className={t("light")} strokeWidth={0} />
      <rect x="8.25" y="7" width="3.5" height="10" rx="1.2" className={t("mid")} strokeWidth={0} />
      <rect x="13.5" y="3" width="3.5" height="14" rx="1.2" className={t("soft")} strokeWidth={0} />
    </>
  ),
  heatmaps: (t) => (
    <>
      <circle cx="10" cy="10" r="7.5" className={t("faint")} strokeWidth={0} />
      <circle cx="10" cy="10" r="4.6" className={t("mid")} strokeWidth={0} />
      <circle cx="10" cy="10" r="2.1" className={t("light")} strokeWidth={0} />
    </>
  ),
  funnels: (t) => (
    <>
      <rect x="2.5" y="3.5" width="15" height="3" rx="1.5" className={t("light")} strokeWidth={0} />
      <rect x="5" y="8.5" width="10" height="3" rx="1.5" className={t("mid")} strokeWidth={0} />
      <rect x="7.5" y="13.5" width="5" height="3" rx="1.5" className={t("soft")} strokeWidth={0} />
    </>
  ),
  attribution: (t) => (
    <>
      <path d="M4.5 15C9 15 8.5 5 15 5" fill="none" className={cn(t("mid"), "fill-none")} strokeWidth={2} strokeLinecap="round" />
      <circle cx="4.5" cy="15" r="2.3" className={t("light")} strokeWidth={0} />
      <circle cx="15" cy="5" r="2.7" className={t("soft")} strokeWidth={0} />
    </>
  ),
  experiments: (t) => (
    <>
      <rect x="3" y="3" width="6" height="14" rx="3" className={t("light")} strokeWidth={0} />
      <rect x="11" y="3" width="6" height="14" rx="3" className={t("mid")} strokeWidth={0} />
    </>
  ),
  api: (t) => (
    <>
      <rect x="9.2" y="2.5" width="1.6" height="4" rx="0.8" className={t("mid")} strokeWidth={0} />
      <rect x="3" y="5.5" width="14" height="11.5" rx="3.6" className={t("mid")} strokeWidth={0} />
      <circle cx="7.4" cy="11.2" r="1.4" className={t("cut")} strokeWidth={0} />
      <circle cx="12.6" cy="11.2" r="1.4" className={t("cut")} strokeWidth={0} />
    </>
  ),
  connectors: (t) => (
    <>
      <rect x="2.5" y="2.5" width="8" height="8" rx="2.5" className={t("light")} strokeWidth={0} />
      <rect x="9.5" y="9.5" width="8" height="8" rx="2.5" className={t("mid")} strokeWidth={0} />
      <rect x="8" y="8" width="4" height="4" rx="1.2" className={t("soft")} strokeWidth={0} />
    </>
  ),
  export: (t) => (
    <>
      <path d="M3.5 12v2.8A2.2 2.2 0 0 0 5.7 17h8.6a2.2 2.2 0 0 0 2.2-2.2V12" className={cn(t("mid"), "fill-none")} strokeWidth={2} strokeLinecap="round" />
      <rect x="9" y="3.5" width="2" height="9" rx="1" className={t("soft")} strokeWidth={0} />
      <path d="M6.5 6.5 10 3l3.5 3.5" className={cn(t("soft"), "fill-none")} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  tools: (t) => (
    <>
      <rect x="3" y="3" width="6" height="6" rx="2" className={t("light")} strokeWidth={0} />
      <rect x="11" y="3" width="6" height="6" rx="2" className={t("light")} strokeWidth={0} />
      <rect x="3" y="11" width="6" height="6" rx="2" className={t("mid")} strokeWidth={0} />
      <rect x="11" y="11" width="6" height="6" rx="3" className={t("soft")} strokeWidth={0} />
    </>
  ),
  resources: (t) => (
    <>
      <rect x="3.5" y="2.5" width="13" height="15" rx="2.6" className={t("mid")} strokeWidth={0} />
      <rect x="6.5" y="9.5" width="7" height="1.8" rx="0.9" className={t("cut")} strokeWidth={0} />
      <rect x="6.5" y="12.8" width="4.5" height="1.8" rx="0.9" className={t("cut")} strokeWidth={0} />
      <path d="M11.5 2.5h3v5.2l-1.5-1.1-1.5 1.1z" className={t("light")} strokeWidth={0} />
    </>
  ),
  clicks: (t) => (
    <>
      <circle cx="8" cy="8" r="5.5" className={t("light")} strokeWidth={0} />
      <circle cx="8" cy="8" r="2.2" className={t("cut")} strokeWidth={0} />
      <path d="M8.6 8.6 17 11.8l-3.6 1.6-1.6 3.6z" className={t("mid")} strokeWidth={1.2} strokeLinejoin="round" />
    </>
  ),
  forms: (t) => (
    <>
      <rect x="3.5" y="2.5" width="13" height="15" rx="2.6" className={t("mid")} strokeWidth={0} />
      <rect x="6.3" y="5.8" width="7.4" height="2" rx="1" className={t("cut")} strokeWidth={0} />
      <rect x="6.3" y="9.3" width="7.4" height="2" rx="1" className={t("cut")} strokeWidth={0} />
      <rect x="6.3" y="12.8" width="4" height="2" rx="1" className={t("cut")} strokeWidth={0} />
    </>
  ),
  revenue: (t) => (
    <>
      <rect x="2.5" y="4.5" width="15" height="11" rx="2.6" className={t("mid")} strokeWidth={0} />
      <rect x="2.5" y="7.2" width="15" height="2.2" className={t("cut")} strokeWidth={0} />
      <rect x="5" y="11.6" width="4.5" height="1.8" rx="0.9" className={t("light")} strokeWidth={0} />
    </>
  ),
  settings: (t) => (
    <>
      <rect x="2.5" y="5.1" width="15" height="1.8" rx="0.9" className={t("light")} strokeWidth={0} />
      <rect x="2.5" y="13.1" width="15" height="1.8" rx="0.9" className={t("light")} strokeWidth={0} />
      <circle cx="7" cy="6" r="2.6" className={t("mid")} strokeWidth={0} />
      <circle cx="13" cy="14" r="2.6" className={t("soft")} strokeWidth={0} />
    </>
  ),
}

export function NavIcon({
  name,
  active = false,
  size = 18,
  className,
}: {
  name: NavIconName
  active?: boolean
  size?: number
  className?: string
}) {
  const palette = active ? ACTIVE : INACTIVE
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
      className={cn("shrink-0 [&_*]:transition-[fill,stroke] [&_*]:duration-150", className)}
    >
      {GLYPHS[name]((tone) => palette[tone])}
    </svg>
  )
}
